import Sidebar from '@/components/layout/sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import {
  Calendar,
  Download,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface TaskAnalysisData {
  date: string;
  totalTasks: number;
  notCompleted: number;
  staffBreakdown: {
    userId: string;
    userName: string;
    notCompleted: number;
  }[];
}

interface TaskAnalysisResponse {
  data: TaskAnalysisData[];
  summary: {
    totalDays: number;
    totalTasksCreated: number;
    totalNotCompleted: number;
    averageTasksPerDay: number;
  };
}

interface TaskDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: number;
  dueDate: string;
  createdAt: string;
}

interface StaffTaskDetails {
  userId: string;
  userName: string;
  date: string;
  tasks: TaskDetail[];
}

interface DateTaskDetails {
  date: string;
  tasks: (TaskDetail & { assignedTo?: string; assignedToName?: string | null })[];
}

export default function TaskAnalysis() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedStaffTasks, setSelectedStaffTasks] =
    useState<StaffTaskDetails | null>(null);
  const [selectedDateTasks, setSelectedDateTasks] =
    useState<DateTaskDetails | null>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isDateTasksView, setIsDateTasksView] = useState(false);
  const [taskFilterStatus, setTaskFilterStatus] = useState<'all' | 'completed' | 'not_completed'>('all');
  const [isUserDatesModalOpen, setIsUserDatesModalOpen] = useState(false);
  const [selectedSummaryUser, setSelectedSummaryUser] = useState<{
    userId: string;
    userName: string;
    dates: { date: string; notCompleted: number }[];
  } | null>(null);

  // Sidebar collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  // Set default date range (last 30 days)
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

  // Load sidebar collapse state from localStorage
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setIsSidebarCollapsed(stored === 'true');
    }
  }, [user?.id]);

  // Save sidebar collapse state to localStorage
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    window.localStorage.setItem(key, String(isSidebarCollapsed));
  }, [isSidebarCollapsed, user?.id]);

  const defaultStartDate = thirtyDaysAgo.toISOString().split('T')[0];
  const defaultEndDate = today.toISOString().split('T')[0];

  const {
    data: analysisData,
    isLoading,
    refetch,
  } = useQuery<TaskAnalysisResponse>({
    queryKey: [
      '/api/analytics/task-analysis',
      startDate || 'all',
      endDate || 'all',
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const qs = params.toString();
      const url = qs
        ? `/api/analytics/task-analysis?${qs}`
        : '/api/analytics/task-analysis';
      const response = await apiRequest('GET', url);
      return response.json();
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to fetch task analysis data',
        variant: 'destructive',
      });
    },
  });

  const handleDateFilter = () => {
    refetch();
  };

  const handleStaffClick = async (
    userId: string,
    userName: string,
    date: string
  ) => {
    setIsLoadingTasks(true);
    setIsTaskModalOpen(true);
    setIsDateTasksView(false);

    try {
      const params = new URLSearchParams();
      params.append('userId', userId);
      params.append('date', date);

      const response = await apiRequest(
        'GET',
        `/api/analytics/task-analysis/staff-tasks?${params.toString()}`
      );
      const data = await response.json();

      setSelectedStaffTasks({
        userId,
        userName,
        date,
        tasks: data.tasks || [],
      });
      setSelectedDateTasks(null);
    } catch (error) {
      console.error('Error fetching staff tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch task details',
        variant: 'destructive',
      });
      setSelectedStaffTasks({
        userId,
        userName,
        date,
        tasks: [],
      });
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const handleTotalTasksClick = async (date: string) => {
    setIsLoadingTasks(true);
    setIsTaskModalOpen(true);
    setIsDateTasksView(true);
    setTaskFilterStatus('all');

    try {
      const params = new URLSearchParams();
      params.append('date', date);

      const response = await apiRequest(
        'GET',
        `/api/analytics/task-analysis/date-tasks?${params.toString()}`
      );
      const data = await response.json();

      setSelectedDateTasks({
        date,
        tasks: data.tasks || [],
      });
      setSelectedStaffTasks(null);
    } catch (error) {
      console.error('Error fetching date tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch task details',
        variant: 'destructive',
      });
      setSelectedDateTasks({
        date,
        tasks: [],
      });
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const handleCompletedTasksClick = async (date: string) => {
    setIsLoadingTasks(true);
    setIsTaskModalOpen(true);
    setIsDateTasksView(true);
    setTaskFilterStatus('completed');

    try {
      const params = new URLSearchParams();
      params.append('date', date);
      params.append('status', 'completed');

      const response = await apiRequest(
        'GET',
        `/api/analytics/task-analysis/date-tasks?${params.toString()}`
      );
      const data = await response.json();

      setSelectedDateTasks({
        date,
        tasks: data.tasks || [],
      });
      setSelectedStaffTasks(null);
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch task details',
        variant: 'destructive',
      });
      setSelectedDateTasks({
        date,
        tasks: [],
      });
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const handleNotCompletedTasksClick = async (date: string) => {
    setIsLoadingTasks(true);
    setIsTaskModalOpen(true);
    setIsDateTasksView(true);
    setTaskFilterStatus('not_completed');

    try {
      const params = new URLSearchParams();
      params.append('date', date);
      params.append('status', 'not_completed');

      const response = await apiRequest(
        'GET',
        `/api/analytics/task-analysis/date-tasks?${params.toString()}`
      );
      const data = await response.json();

      setSelectedDateTasks({
        date,
        tasks: data.tasks || [],
      });
      setSelectedStaffTasks(null);
    } catch (error) {
      console.error('Error fetching not completed tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch task details',
        variant: 'destructive',
      });
      setSelectedDateTasks({
        date,
        tasks: [],
      });
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const handleSummaryUserClick = (userId: string, userName: string) => {
    if (!analysisData?.data) return;
    const dates: { date: string; notCompleted: number }[] = [];
    analysisData.data.forEach(day => {
      const staffEntry = day.staffBreakdown.find(s => s.userId === userId);
      if (staffEntry && staffEntry.notCompleted > 0) {
        dates.push({ date: day.date, notCompleted: staffEntry.notCompleted });
      }
    });
    setSelectedSummaryUser({ userId, userName, dates });
    setIsUserDatesModalOpen(true);
  };

  const exportToCSV = () => {
    if (!analysisData?.data) return;

    const csvContent = [
      ['Date', 'Total Tasks Created', 'Not Completed', 'Staff Breakdown'],
      ...analysisData.data.map(day => [
        day.date,
        day.totalTasks.toString(),
        day.notCompleted.toString(),
        day.staffBreakdown
          .map(staff => `${staff.userName}: ${staff.notCompleted}`)
          .join(', '),
      ]),
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `task-analysis-${startDate || 'all'}-to-${endDate || 'all'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Export Successful',
      description: 'Task analysis data exported to CSV',
    });
  };

  const exportToPDF = () => {
    if (!analysisData?.data) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredHeight: number) => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Task Analysis Report', margin, yPosition);
    yPosition += 15;

    // Date range
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    let dateRange = 'All time';
    if (startDate && endDate) {
      dateRange = `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      dateRange = `From ${formatDate(startDate)}`;
    } else if (endDate) {
      dateRange = `Until ${formatDate(endDate)}`;
    }
    doc.text(`Date Range: ${dateRange}`, margin, yPosition);
    yPosition += 10;

    // Summary
    doc.text(
      `Total Days Analyzed: ${analysisData.summary.totalDays}`,
      margin,
      yPosition
    );
    yPosition += 8;
    doc.text(
      `Total Tasks Created: ${analysisData.summary.totalTasksCreated}`,
      margin,
      yPosition
    );
    yPosition += 8;
    doc.text(
      `Total Not Completed: ${analysisData.summary.totalNotCompleted}`,
      margin,
      yPosition
    );
    yPosition += 8;
    doc.text(
      `Average Tasks Per Day: ${analysisData.summary.averageTasksPerDay.toFixed(1)}`,
      margin,
      yPosition
    );
    yPosition += 15;

    // Table headers
    const tableHeaders = [
      'Date',
      'Total Tasks',
      'Not Completed',
      'Staff Breakdown',
    ];
    const columnWidths = [25, 25, 40, 50, 70];
    const rowHeight = 12;

    // Draw table headers
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    let xPosition = margin;

    tableHeaders.forEach((header, index) => {
      doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);
      doc.text(header, xPosition + 2, yPosition + 5);
      xPosition += columnWidths[index];
    });
    yPosition += rowHeight;

    // Draw table rows
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    analysisData.data
      .filter(day => day.notCompleted > 0)
      .forEach(day => {
        checkPageBreak(rowHeight);

        xPosition = margin;
        const rowData = [
          formatDate(day.date),
          day.totalTasks.toString(),
          day.notCompleted.toString(),
          day.staffBreakdown
            .map(staff => `${staff.userName}: ${staff.notCompleted}`)
            .join(', '),
        ];

        rowData.forEach((cell, index) => {
          doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);

          // Wrap text if too long
          const maxWidth = columnWidths[index] - 4;
          const lines = doc.splitTextToSize(cell, maxWidth);
          doc.text(lines, xPosition + 2, yPosition + 5);

          xPosition += columnWidths[index];
        });

        yPosition += rowHeight;
      });

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${totalPages}`,
        margin,
        pageHeight - 10
      );
    }

    // Save the PDF
    const fileName = `task-analysis-${startDate || 'all'}-to-${endDate || 'all'}.pdf`;
    doc.save(fileName);

    toast({
      title: 'Export Successful',
      description: 'Task analysis data exported to PDF',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {!isSidebarCollapsed && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Sidebar Toggle */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Sidebar Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="p-2 h-8 w-8"
                aria-label={
                  isSidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'
                }
              >
                {isSidebarCollapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </Button>

              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <FileText className="w-6 h-6 mr-2 text-blue-600" />
                  Task Analysis Report
                </h1>
                <p className="text-gray-600 text-sm">
                  Daily task creation and completion analysis with staff-wise
                  breakdown
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={exportToCSV}
                disabled={!analysisData?.data}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={exportToPDF}
                disabled={!analysisData?.data}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Date Filters */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Date Range Filter
                </CardTitle>
                <CardDescription>
                  Select the date range for task analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="mt-1"
                      placeholder={defaultStartDate}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="mt-1"
                      placeholder={defaultEndDate}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleDateFilter}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Apply Filter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Summary Card */}
            {analysisData?.data && analysisData.data.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Total Summary
                  </CardTitle>
                  <CardDescription>
                    Overall totals across all dates in the selected range
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Days Analyzed */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {analysisData.summary.totalDays}
                      </div>
                      <div className="text-sm text-gray-600">
                        Days Analyzed
                      </div>
                    </div>

                    {/* Total Tasks Created */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        {analysisData.summary.totalTasksCreated}
                      </div>
                      <div className="text-sm text-gray-600">
                        Total Tasks Created
                      </div>
                    </div>

                    {/* Total Not Completed */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-red-600 mb-2">
                        {analysisData.summary.totalNotCompleted}
                      </div>
                      <div className="text-sm text-gray-600">
                        Not Completed
                      </div>
                    </div>

                    {/* Completion Rate */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-2">
                        {analysisData.summary.totalTasksCreated > 0
                          ? Math.round(
                              ((analysisData.summary.totalTasksCreated -
                                analysisData.summary.totalNotCompleted) /
                                analysisData.summary.totalTasksCreated) *
                                100
                            )
                          : 0}
                        %
                      </div>
                      <div className="text-sm text-gray-600">
                        Completed Rate
                      </div>
                    </div>
                  </div>

                  {/* User-wise Pending Tasks Summary */}
                  {(() => {
                    // Aggregate user-wise pending tasks across all dates
                    const userTotals = new Map<
                      string,
                      { userId: string; userName: string; totalPending: number }
                    >();

                    analysisData.data.forEach(day => {
                      day.staffBreakdown.forEach(staff => {
                        if (!userTotals.has(staff.userId)) {
                          userTotals.set(staff.userId, {
                            userId: staff.userId,
                            userName: staff.userName,
                            totalPending: 0,
                          });
                        }
                        userTotals.get(staff.userId)!.totalPending +=
                          staff.notCompleted;
                      });
                    });

                    const userTotalsArray = Array.from(
                      userTotals.values()
                    ).sort((a, b) => b.totalPending - a.totalPending);

                    return userTotalsArray.length > 0 ? (
                      <div className="mt-6 pt-6 border-t">
                        <h4 className="font-medium text-gray-900 mb-4">
                          User-wise Pending Tasks
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {userTotalsArray.map((user, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-sm px-3 py-1 bg-orange-50 text-orange-700 border-orange-200 cursor-pointer select-none hover:bg-orange-100 focus:ring-2 focus:ring-orange-300"
                              onClick={() =>
                                handleSummaryUserClick(
                                  user.userId,
                                  user.userName
                                )
                              }
                              role="button"
                              title={`View ${user.userName}'s pending tasks by date`}
                            >
                              {user.userName}: {user.totalPending}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </CardContent>
              </Card>
            )}

            {/* Analysis Table */}
            <Card>
              <CardHeader>
                <CardTitle>Daily Task Analysis</CardTitle>
                <CardDescription>
                  Days with incomplete tasks - breakdown of tasks created and
                  completion status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading task analysis...</span>
                  </div>
                ) : !analysisData?.data ||
                  analysisData.data.filter(day => day.notCompleted > 0)
                    .length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Incomplete Tasks Found
                    </h3>
                    <p className="text-gray-600">
                      All tasks are completed for the selected date range
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-medium">Created Date</th>
                          <th className="text-left p-3 font-medium">
                            Total Tasks
                          </th>
                          <th className="text-left p-3 font-medium">
                            Completed
                          </th>
                          <th className="text-left p-3 font-medium">
                            Not Completed
                          </th>
                          <th className="text-left p-3 font-medium">
                            Staff Breakdown (Not Completed)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisData.data
                          .filter(day => day.notCompleted > 0) // Only show days with incomplete tasks
                          .map((day, index) => (
                            <tr
                              key={index}
                              className="border-b hover:bg-gray-50"
                            >
                              <td className="p-3 font-medium">
                                {formatDate(day.date)}
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant="outline"
                                  className="bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100 hover:text-blue-800 transition-colors"
                                  onClick={() => handleTotalTasksClick(day.date)}
                                  title={`Click to view all ${day.totalTasks} tasks created on this date`}
                                >
                                  {day.totalTasks}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant="outline"
                                  className="bg-green-50 text-green-700 cursor-pointer hover:bg-green-100 hover:text-green-800 transition-colors"
                                  onClick={() => handleCompletedTasksClick(day.date)}
                                  title={`Click to view ${day.totalTasks - day.notCompleted} completed tasks`}
                                >
                                  {day.totalTasks - day.notCompleted}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant={
                                    day.notCompleted > 0
                                      ? 'destructive'
                                      : 'default'
                                  }
                                  className={`cursor-pointer hover:opacity-80 transition-opacity ${
                                    day.notCompleted === 0
                                      ? 'bg-green-50 text-green-700'
                                      : ''
                                  }`}
                                  onClick={() => handleNotCompletedTasksClick(day.date)}
                                  title={`Click to view ${day.notCompleted} not completed tasks`}
                                >
                                  {day.notCompleted}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {day.staffBreakdown.length === 0 ? (
                                    <span className="text-gray-500 text-sm">
                                      No incomplete tasks
                                    </span>
                                  ) : (
                                    day.staffBreakdown.map(
                                      (staff, staffIndex) => (
                                        <Badge
                                          key={staffIndex}
                                          variant="secondary"
                                          className="text-xs cursor-pointer hover:bg-blue-100 hover:text-blue-800 transition-colors"
                                          onClick={() =>
                                            handleStaffClick(
                                              staff.userId,
                                              staff.userName,
                                              day.date
                                            )
                                          }
                                          title={`Click to view ${staff.userName}'s incomplete tasks`}
                                        >
                                          {staff.userName}: {staff.notCompleted}
                                        </Badge>
                                      )
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Task Details Modal */}
            <Dialog open={isTaskModalOpen} onOpenChange={setIsTaskModalOpen}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {isDateTasksView ? (
                      <>
                        <FileText className="h-5 w-5" />
                        {taskFilterStatus === 'completed'
                          ? 'Completed Tasks'
                          : taskFilterStatus === 'not_completed'
                            ? 'Not Completed Tasks'
                            : 'All Tasks'}
                      </>
                    ) : (
                      <>
                        <User className="h-5 w-5" />
                        {selectedStaffTasks
                          ? `${selectedStaffTasks.userName}'s Tasks`
                          : 'Task Details'}
                      </>
                    )}
                  </DialogTitle>
                  <DialogDescription>
                    {isDateTasksView && selectedDateTasks
                      ? `${taskFilterStatus === 'completed'
                          ? 'Completed'
                          : taskFilterStatus === 'not_completed'
                            ? 'Not completed'
                            : 'All'} tasks created on ${formatDate(selectedDateTasks.date)}`
                      : selectedStaffTasks &&
                        `Incomplete tasks for ${selectedStaffTasks.userName} on ${formatDate(selectedStaffTasks.date)}`}
                  </DialogDescription>
                </DialogHeader>

                {isLoadingTasks ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading task details...</span>
                  </div>
                ) : isDateTasksView && selectedDateTasks ? (
                  <div className="space-y-4">
                    {selectedDateTasks.tasks.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No Tasks
                        </h3>
                        <p className="text-gray-600">
                          {taskFilterStatus === 'completed'
                            ? 'No completed tasks were found on this date.'
                            : taskFilterStatus === 'not_completed'
                              ? 'No incomplete tasks were found on this date.'
                              : 'No tasks were created on this date.'}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700"
                            >
                              {selectedDateTasks.tasks.length} Tasks
                            </Badge>
                            <span className="text-sm text-gray-600">
                              Created on {formatDate(selectedDateTasks.date)}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {selectedDateTasks.tasks.map(task => (
                            <Card
                              key={task.id}
                              className="border-l-4 border-l-blue-500"
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-900 mb-2">
                                      {task.title}
                                    </h4>
                                    {task.description && (
                                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                        {task.description}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      {task.assignedToName && (
                                        <div className="flex items-center gap-1">
                                          <User className="h-3 w-3" />
                                          <span>{task.assignedToName}</span>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-1">
                                        <Badge
                                          variant="outline"
                                          className={`text-xs ${
                                            task.status === 'pending'
                                              ? 'bg-yellow-50 text-yellow-700'
                                              : task.status === 'started'
                                                ? 'bg-blue-50 text-blue-700'
                                                : task.status === 'working'
                                                  ? 'bg-green-50 text-green-700'
                                                  : task.status ===
                                                      'need_more_time'
                                                    ? 'bg-orange-50 text-orange-700'
                                                    : task.status ===
                                                        'need_help'
                                                      ? 'bg-red-50 text-red-700'
                                                      : task.status ===
                                                          'pending_approval'
                                                        ? 'bg-purple-50 text-purple-700'
                                                        : task.status ===
                                                            'complete'
                                                          ? 'bg-green-50 text-green-700'
                                                          : task.status ===
                                                              'dropped'
                                                            ? 'bg-gray-50 text-gray-700'
                                                            : 'bg-gray-50 text-gray-700'
                                          }`}
                                        >
                                          {task.status
                                            .replace('_', ' ')
                                            .toUpperCase()}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <span>Priority:</span>
                                        <Badge
                                          variant="outline"
                                          className="bg-blue-50 text-blue-700"
                                        >
                                          #{(task.priority || 0) + 1}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        <span>
                                          Due: {formatDate(task.dueDate)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : selectedStaffTasks ? (
                  <div className="space-y-4">
                    {selectedStaffTasks.tasks.length === 0 ? (
                      <div className="text-center py-8">
                        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No Incomplete Tasks
                        </h3>
                        <p className="text-gray-600">
                          {selectedStaffTasks.userName} has no incomplete tasks
                          for this date.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700"
                            >
                              {selectedStaffTasks.tasks.length} Tasks
                            </Badge>
                            <span className="text-sm text-gray-600">
                              Created on {formatDate(selectedStaffTasks.date)}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {selectedStaffTasks.tasks.map(task => (
                            <Card
                              key={task.id}
                              className="border-l-4 border-l-blue-500"
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-900 mb-2">
                                      {task.title}
                                    </h4>
                                    {task.description && (
                                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                        {task.description}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      <div className="flex items-center gap-1">
                                        <Badge
                                          variant="outline"
                                          className={`text-xs ${
                                            task.status === 'pending'
                                              ? 'bg-yellow-50 text-yellow-700'
                                              : task.status === 'started'
                                                ? 'bg-blue-50 text-blue-700'
                                                : task.status === 'working'
                                                  ? 'bg-green-50 text-green-700'
                                                  : task.status ===
                                                      'need_more_time'
                                                    ? 'bg-orange-50 text-orange-700'
                                                    : task.status ===
                                                        'need_help'
                                                      ? 'bg-red-50 text-red-700'
                                                      : task.status ===
                                                          'pending_approval'
                                                        ? 'bg-purple-50 text-purple-700'
                                                        : task.status ===
                                                            'complete'
                                                          ? 'bg-green-50 text-green-700'
                                                          : task.status ===
                                                              'dropped'
                                                            ? 'bg-gray-50 text-gray-700'
                                                            : 'bg-gray-50 text-gray-700'
                                          }`}
                                        >
                                          {task.status
                                            .replace('_', ' ')
                                            .toUpperCase()}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <span>Priority:</span>
                                        <Badge
                                          variant="outline"
                                          className="bg-blue-50 text-blue-700"
                                        >
                                          #{(task.priority || 0) + 1}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        <span>
                                          Due: {formatDate(task.dueDate)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}

                <div className="flex justify-end pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setIsTaskModalOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Summary User Dates Modal */}
            <Dialog
              open={isUserDatesModalOpen}
              onOpenChange={setIsUserDatesModalOpen}
            >
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Select a date</DialogTitle>
                  <DialogDescription>
                    {selectedSummaryUser
                      ? `View incomplete tasks for ${selectedSummaryUser.userName}`
                      : ''}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                  {selectedSummaryUser?.dates.map(({ date, notCompleted }) => (
                    <Button
                      key={date}
                      variant="outline"
                      className="w-full flex justify-between"
                      onClick={() => {
                        setIsUserDatesModalOpen(false);
                        handleStaffClick(
                          selectedSummaryUser.userId,
                          selectedSummaryUser.userName,
                          date
                        );
                      }}
                    >
                      <span>{formatDate(date)}</span>
                      <Badge
                        variant="outline"
                        className="bg-orange-50 text-orange-700"
                      >
                        {notCompleted} pending
                      </Badge>
                    </Button>
                  ))}
                  {selectedSummaryUser &&
                    selectedSummaryUser.dates.length === 0 && (
                      <div className="text-center text-sm text-gray-600 py-4">
                        No pending tasks in the selected range.
                      </div>
                    )}
                </div>
                <div className="flex justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsUserDatesModalOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
